import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useUpdateTrial } from "./useTrial";
import { useNewTrialState } from "./NewTrialStateProvider";
import { buildPlotDesignApiPayload } from "./plotDesignPayload";
import { useDebouncedAsyncCallback } from "../../../shared/custom-hooks/useDebouncedAsyncCallback";
import {
  isValidTrialId,
  ensureValidTrialId,
} from "../../../shared/utils/trialValidation";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { type NewTrialStateInterface } from "../types";

// Trailing-edge window a burst of deletes collapses into a single PATCH.
const PERSIST_DEBOUNCE_MS = 600;

export interface UsePersistPlotDesignOnDeleteInterface {
  persistPlotDesignOnDelete: (nextState: NewTrialStateInterface) => void;
}

/**
 * Persists a Plot Design delete (trial_plots/ab_line/buffer_zone/
 * machinery_grid) immediately, reusing the same PATCH payload shape the
 * wizard's Save/Next step sends (`buildPlotDesignApiPayload`) -- but WITHOUT
 * `completed_step`, so a delete never advances or marks the Plot Design step
 * complete on its own.
 *
 * `persistPlotDesignOnDelete` must be called with the state AFTER the delete
 * has already been applied (not the stale pre-delete `newTrialState` closed
 * over at click time), since `setNewTrialState` updates are async/batched.
 */
export const usePersistPlotDesignOnDelete =
  (): UsePersistPlotDesignOnDeleteInterface => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { trialInfo } = useNewTrialState();
    const updateTrialMutation = useUpdateTrial();
    const effectiveTrialId = id ?? trialInfo?.id;

    const persist = async (
      nextState: NewTrialStateInterface
    ): Promise<void> => {
      if (!isValidTrialId(effectiveTrialId)) {
        console.error(
          formatTranslation(t("newTrial.error.invalidTrialId")),
          "usePersistPlotDesignOnDelete"
        );
        return;
      }
      const trialId = ensureValidTrialId(
        effectiveTrialId,
        "usePersistPlotDesignOnDelete"
      );
      const payload = buildPlotDesignApiPayload(nextState, trialInfo, t);

      try {
        const updatedTrial = await updateTrialMutation.mutateAsync({
          trialId,
          payload,
        });
        if (!updatedTrial) {
          toast.error(formatTranslation(t("common.error.update")));
        }
      } catch (err) {
        toast.error(formatTranslation(t("common.error.update")));
        console.error(
          formatTranslation(t("common.error.console.trialUpdate")),
          err
        );
      }
    };

    const persistPlotDesignOnDelete = useDebouncedAsyncCallback(
      persist,
      PERSIST_DEBOUNCE_MS
    );

    return { persistPlotDesignOnDelete };
  };
